// index.jsx — CommandPaletteRoot: the single global mount for the ⌘K palette.
// WL (Fable Hail Mary Wave 5). Mounted once in App.js. Owns open/close state, the
// Ctrl/⌘+K hotkey, focus capture+restore, and an unobtrusive launcher pill for
// mouse users. ADDITIVE: adds a new overlay + one quiet fixed affordance; it
// changes no existing navigation, route, or component.

import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "../../utils";
import { fontFamily, field, radius, shadow, spacing, zIndex, focusRing } from "../../theme/tokens";
import { isUserLoggedIn } from "../../utils/helpers";
import { IconSearch } from "./icons";
import CommandPalette from "./CommandPalette";

// Detect the platform once for the correct modifier glyph in the hint.
const isMac =
  typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform || "");
const MOD_LABEL = isMac ? "⌘" : "Ctrl";

const Launcher = styled.button`
  position: fixed;
  left: ${spacing.lg}px;
  bottom: ${spacing.lg}px;
  z-index: ${zIndex.sticky};
  display: none; /* shown on pointer-capable, desktop-width screens only */
  align-items: center;
  gap: ${spacing.sm}px;
  min-height: 44px;
  padding: 0 14px;
  background: ${colors.white};
  border: 1px solid ${colors.lightBorder};
  border-radius: ${radius.pill}px;
  box-shadow: ${shadow.e1};
  color: ${field.muted};
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  /* A11y (WCAG 1.4.3 AA): resting opacity used to be 0.9, which blends
     field.muted (#667085, 4.97:1 on white) down to an effective ~#757e91
     (4.07:1) — below the 4.5:1 floor for the "Search" label text. Full
     opacity at rest keeps the label AA-compliant; hover/focus already
     strengthen the border/shadow for the "clearer on interaction" affordance. */
  opacity: 1;
  transition: opacity 150ms ease, box-shadow 150ms ease, border-color 150ms ease;

  &:hover {
    opacity: 1;
    box-shadow: ${shadow.e2};
    border-color: #d7c4fb;
    color: ${field.ink};
  }
  &:focus-visible {
    outline: none;
    opacity: 1;
    box-shadow: ${focusRing.shadow};
  }

  @media (min-width: 900px) and (pointer: fine) {
    display: inline-flex;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const LauncherKbd = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  font-weight: 600;
  /* A11y (WCAG 1.4.3 AA): the CtrlK hint text (brand.primary #9B5AF8) on #f1e9fe
     was 3.42:1. PrimaryPurple700 (#8126DB) clears 4.5:1 (5.51:1). */
  color: ${colors.PrimaryPurple700};
  background: #f1e9fe;
  border-radius: ${radius.sm}px;
  padding: 2px 6px;
`;

const CommandPaletteRoot = () => {
  const [open, setOpen] = useState(false);
  const restoreRef = useRef(null);

  const openPalette = useCallback(() => {
    if (!isUserLoggedIn()) return;
    restoreRef.current = document.activeElement;
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    // Restore focus to wherever the user was before opening.
    const el = restoreRef.current;
    if (el && typeof el.focus === "function") {
      requestAnimationFrame(() => el.focus());
    }
  }, []);

  // Global Ctrl/⌘+K toggle.
  useEffect(() => {
    const onKeyDown = (e) => {
      const key = (e.key || "").toLowerCase();
      if (key === "k" && (e.metaKey || e.ctrlKey) && !e.altKey) {
        if (!isUserLoggedIn()) return;
        e.preventDefault();
        setOpen((prev) => {
          if (prev) {
            // toggling closed — mirror closePalette's focus restore
            const el = restoreRef.current;
            if (el && typeof el.focus === "function") {
              requestAnimationFrame(() => el.focus());
            }
            return false;
          }
          restoreRef.current = document.activeElement;
          return true;
        });
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const loggedIn = isUserLoggedIn();

  return (
    <>
      {loggedIn && (
        <Launcher
          type="button"
          onClick={openPalette}
          aria-haspopup="dialog"
          aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
          aria-label={`Open command palette (${MOD_LABEL} K)`}
          title={`Search — ${MOD_LABEL}K`}
        >
          <IconSearch size={16} aria-hidden="true" />
          <span>Search</span>
          <LauncherKbd aria-hidden="true">{MOD_LABEL}K</LauncherKbd>
        </Launcher>
      )}
      <CommandPalette open={open} onClose={closePalette} />
    </>
  );
};

export default CommandPaletteRoot;
